<template>
  <!-- Reset the whole skeleton if this changes so the animation never goes out of sync  -->
  <div
    :key="rowCount"
    inert
    :class="[
      `relative flex flex-col overflow-y-auto`,
      isHeaderShown ? `pb-4` : `pb-4 pt-2 lg:pt-4`,
    ]"
  >
    <table class="w-full">
      <thead v-if="isHeaderShown">
        <tr class="text-xs font-bold uppercase tracking-wider">
          <th
            v-for="(header, idx) in headers"
            :key="header.id"
            class="p-2 first:pl-4 last:pr-4"
          >
            <div
              v-if="!isSelectionOrActionId(header.column.id)"
              class="flex size-full items-center whitespace-nowrap"
            >
              <span
                class="min-w-5 animate-pulse rounded-md bg-gray-300 text-transparent dark:bg-card"
                :style="{
                  'animation-delay': `${
                    (ANIMATION_DURATION / headers.length) * idx
                  }ms`,
                }"
              >
                <FlexRender
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
              </span>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(_, i) in rowCount" :key="i">
          <td
            v-for="(header, j) in headers"
            :key="header.id"
            class="px-2 py-1 font-bold leading-6 first:pl-4 last:pr-4"
            :class="[
              isActionId(header.column.id)
                ? `px-4`
                : isPrimaryColumn(header.column) && `w-full`,
            ]"
          >
            <div
              class="my-px h-4 animate-pulse rounded-md bg-gray-300 dark:bg-card"
              :style="{
                width: isSelectionOrActionId(header.column.id)
                  ? `16px`
                  : isPrimaryColumn(header.column)
                  ? `${5 * getRandomIntInclusive(4, 8)}%`
                  : `96px`,
                'animation-delay': `${
                  (ANIMATION_DURATION / rowCount) * (i + 1) +
                  (ANIMATION_DURATION / headers.length) * j
                }ms`,
              }"
            ></div>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      class="absolute inset-0 bg-gradient-to-b from-transparent to-white dark:to-background"
    ></div>
  </div>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import { FlexRender } from "@tanstack/vue-table";
import { computed } from "vue";

import { useMediaQuery } from "@/lib/composables/media-query";
import { getRandomIntInclusive } from "@/lib/random";
import {
  type InternalTableInstance,
  isPrimaryColumn,
} from "@/lib/tanstack-table";
import type { GenericModel } from "@/types/model";

const ANIMATION_DURATION = 2000;

const props = withDefaults(
  defineProps<{
    table: InternalTableInstance<Model>;
    hideHeader?: boolean;
    rowCount?: number;
  }>(),
  {
    hideHeader: false,
    rowCount: 20,
  }
);

const table = props.table;
const instance = table.instance;

const headers = computed(() => instance.getFlatHeaders());

const isMobile = useMediaQuery(`(max-width: 1023px)`);
const isHeaderShown = computed(() => {
  return !props.hideHeader && !isMobile.value;
});

const isActionId = (id: string) => {
  return id === "$$actions";
};

const isSelectionOrActionId = (id: string) => {
  return id === "$$selection" || id === "$$actions";
};
</script>
